import React, { useRef, useEffect } from 'react';
import { marked } from 'marked';
let katex = null;
import('katex').then(m => { katex = m.default; });
import 'katex/dist/katex.min.css';

const FILTER_LABELS = [
    { id: 'header', label: 'Header' },
    { id: 'header_image', label: 'Header Image' },
    { id: 'footer', label: 'Footer' },
    { id: 'footer_image', label: 'Footer Image' },
    { id: 'number', label: 'Page Number' },
    { id: 'footnote', label: 'Footnote' },
    { id: 'aside_text', label: 'Aside Text' },
];

const BLOCK_COLORS = {
    paragraph_title: '#4658ff', doc_title: '#4658ff', text: '#4658ff',
    table: '#2FBD71',
    display_formula: '#fadb14', inline_formula: '#fadb14',
    image: '#BD4CFF', chart: '#BD4CFF',
    seal: '#F94E28', algorithm: '#F94E28',
    header: '#b6b2f1', footer: '#b6b2f1', header_image: '#b6b2f1',
    footer_image: '#b6b2f1', number: '#b6b2f1', footnote: '#b6b2f1', aside_text: '#b6b2f1',
};

const LABEL_NAMES = {
    paragraph_title: 'Title', doc_title: 'Doc title', text: 'Text',
    table: 'Table', display_formula: 'Display formula', inline_formula: 'Inline formula',
    image: 'Image', chart: 'Chart', seal: 'Seal', algorithm: 'Algorithm',
    header: 'Header', footer: 'Footer', header_image: 'Header image',
    footer_image: 'Footer image', number: 'Page number', footnote: 'Footnote', aside_text: 'Aside text',
};

const HELPER_LABELS = new Set(['header', 'footer', 'header_image', 'footer_image', 'number', 'footnote', 'aside_text', 'formula_number']);

const PROSE = "prose prose-sm max-w-none break-words prose-headings:text-gray-900 prose-h1:text-xl prose-h1:border-b prose-h1:border-gray-200 prose-h1:pb-2 prose-h2:text-lg prose-h3:text-base prose-p:text-gray-700 prose-p:leading-relaxed prose-table:text-sm prose-th:bg-gray-50 prose-th:text-gray-600 prose-td:text-gray-700 [&_img]:rounded-lg [&_img]:border [&_img]:border-gray-200 [&_img]:max-w-full [&_img]:h-auto [&_img]:min-w-[40px] [&_table]:border-collapse [&_table]:w-full [&_table]:text-sm [&_table]:table-fixed [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_td]:break-words [&_th]:border [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-gray-50 [&_pre]:overflow-x-auto [&_pre]:max-w-full";

function filterMarkdownByBlocks(markdown, blocks, filters) {
    if (!blocks?.length || !markdown) return markdown;
    const hidden = new Set(FILTER_LABELS.filter(f => !filters[f.id]).map(f => f.id));
    if (!hidden.size) return markdown;
    let result = markdown;
    for (const block of blocks) {
        if (hidden.has(block.block_label) && block.block_content) {
            const content = block.block_content.trim();
            if (content) result = result.replace(content, '');
        }
    }
    return result;
}

function renderLatex(text, displayMode) {
    if (!katex) return displayMode ? `<div class="katex-display">$$${text}$$</div>` : `$${text}$`;
    try {
        return katex.renderToString(text, { displayMode, throwOnError: false, strict: false });
    } catch {
        return displayMode ? `$$${text}$$` : `$${text}$`;
    }
}

marked.setOptions({ breaks: true, gfm: true });

function renderMarkdown(md) {
    if (!md) return '';
    let html = marked.parse(md);
    html = html.replace(/\$\$([^$]+?)\$\$/g, (_, tex) => renderLatex(tex.trim(), true));
    html = html.replace(/(?<!\$)\$([^$<>\n]{2,}?)\$(?!\$)/g, (match, tex) => {
        if (/^[\d.,]+$/.test(tex.trim())) return match;
        if (/<|>|&lt;|&gt;|src=|href=/.test(tex)) return match;
        return renderLatex(tex.trim(), false);
    });
    return html;
}

const IMAGE_BLOCK_LABELS = new Set(['image', 'chart', 'seal', 'header_image', 'footer_image']);

function BlocksList({ blocks, md, pageIdx, activePage, hoveredBlockIdx, selectedBlockIdx, onBlockHover, onBlockClick, blockRefs, filters }) {
    return blocks.map((block, bi) => {
        if (block.global_block_id !== undefined && block.global_group_id !== undefined
            && block.global_block_id !== block.global_group_id) return null;
        const hidden = FILTER_LABELS.some(f => f.id === block.block_label && !(filters[f.id] ?? true));
        if (hidden) return null;

        const isImageBlock = IMAGE_BLOCK_LABELS.has(block.block_label);
        const content = block.block_content || '';
        if (!content.trim() && !isImageBlock) return null;

        const displayContent = content.trim() ? content : (isImageBlock ? `*[${LABEL_NAMES[block.block_label] || block.block_label}]*` : '');

        const isHovered = pageIdx === activePage && bi === hoveredBlockIdx;
        const isSelected = pageIdx === activePage && bi === selectedBlockIdx;
        const isActive = isHovered || isSelected;
        const color = BLOCK_COLORS[block.block_label] || '#4658ff';
        const label = LABEL_NAMES[block.block_label] || block.block_label;
        const isHelper = HELPER_LABELS.has(block.block_label);

        return (
            <div
                key={bi}
                ref={el => { if (pageIdx === activePage) blockRefs.current[bi] = el; }}
                className="relative cursor-pointer"
                style={{
                    padding: '8px 12px',
                    marginBottom: '2px',
                    borderLeft: isSelected ? '3px solid #2E77FF' : isHovered ? '3px solid #fadb14' : '3px solid transparent',
                    backgroundColor: isSelected ? 'rgba(46,119,255,0.06)' : isHovered ? 'rgba(250,219,20,0.06)' : 'transparent',
                    borderRadius: '4px',
                    transition: 'background-color 0.15s, border-color 0.15s',
                }}
                onMouseEnter={() => { if (pageIdx === activePage) onBlockHover?.(bi); }}
                onMouseLeave={() => onBlockHover?.(null)}
                onClick={() => { if (pageIdx === activePage) onBlockClick?.(bi); }}
            >
                {isActive && (
                    <span
                        className="absolute left-3 text-white text-[11px] font-medium px-1.5 leading-5 rounded-sm"
                        style={{ top: '-10px', backgroundColor: isHelper ? '#b6b2f1' : color, zIndex: 1 }}
                    >
                        {label}
                    </span>
                )}
                <div className={PROSE} dangerouslySetInnerHTML={{ __html: renderMarkdown(displayContent) }} />
            </div>
        );
    });
}

export default function OcrPageView({
    pages,
    filters,
    activePage,
    hoveredBlockIdx,
    selectedBlockIdx,
    onBlockHover,
    onBlockClick,
}) {
    const containerRef = useRef(null);
    const pageRefs = useRef({});
    const blockRefs = useRef({});

    useEffect(() => {
        if (activePage != null && pageRefs.current[activePage]) {
            pageRefs.current[activePage].scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [activePage]);

    useEffect(() => {
        if (selectedBlockIdx != null && blockRefs.current[selectedBlockIdx]) {
            blockRefs.current[selectedBlockIdx].scrollIntoView({ behavior: 'instant', block: 'center' });
        }
    }, [selectedBlockIdx]);

    return (
        <div className="flex-1 overflow-y-auto" ref={containerRef}>
            {(pages || []).map((page, pageIdx) => {
                const pageBlocks = page?.blocks || [];
                const hasBlocks = pageBlocks.length > 0;
                const md = filterMarkdownByBlocks(page?.markdown || '', pageBlocks, filters);

                return (
                    <div key={pageIdx} ref={el => pageRefs.current[pageIdx] = el}>
                        <div className="px-4 py-4">
                            {hasBlocks ? (
                                <BlocksList
                                    blocks={pageBlocks}
                                    md={md}
                                    pageIdx={pageIdx}
                                    activePage={activePage}
                                    hoveredBlockIdx={hoveredBlockIdx}
                                    selectedBlockIdx={selectedBlockIdx}
                                    onBlockHover={onBlockHover}
                                    onBlockClick={onBlockClick}
                                    blockRefs={blockRefs}
                                    filters={filters}
                                />
                            ) : (
                                <div className={PROSE} dangerouslySetInnerHTML={{ __html: renderMarkdown(md) }} />
                            )}
                        </div>
                        <div className="flex items-center gap-3 px-6 pb-2 pt-1">
                            <div className="flex-1 h-px" style={{ background: '#e1e6f8' }} />
                            <span style={{ fontSize: '11px', color: '#9498ac' }}>
                                Page {page.pageIndex != null ? page.pageIndex + 1 : pageIdx + 1}
                            </span>
                            <div className="flex-1 h-px" style={{ background: '#e1e6f8' }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export { FILTER_LABELS };
